import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';

// champisDisplayName is registered globally on the User model by
// champis-users' own extend.js - see champis-species' authorDisplayName.ts
// for the same augmentation with the full explanation. Declared separately
// here too since each extension is its own TypeScript compilation unit.

// app.modal.show()'s own first parameter type (UnsafeModalClass |
// AsyncModalClass) isn't exported from flarum/core - extracted through
// Parameters<> instead of naming it directly, so this stays correct even
// if that internal type is ever renamed.

// champis-forum's "Modifier" (components/EditPostButton): the edit composer
// for the article's text, for whoever may edit it - looked up at render
// time through flarum.reg, same reason as PostImageLightbox below.
function editPostButton(post) {
  const EditPostButton = window.flarum.reg.get('champis-forum', 'forum/components/EditPostButton');
  return EditPostButton ? m(EditPostButton, {
    post
  }) : null;
}

/**
 * /article/:id - a single "articles"-tagged discussion, presented as a
 * standalone page (title + author + post body, no reply/comment UI)
 * rather than a regular discussion thread - matching champis.net's own
 * /article/:id, which shows just the article itself. Linked from the
 * "Articles" header dropdown (see ArticlesMenu.js), not gated by tag here
 * (same as champis-species' DescriptionPage.js) - only reachable in
 * practice through links the menu itself builds. The rules and help pages
 * too (champis-help_rules' "Règles et aide" menu).
 *
 * "Modifier" after the author, for whoever may edit the text.
 */
export default class ArticlePage extends Page {
  discussion = null;
  notFound = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.discussion = null;
    this.notFound = false;
    this.load(m.route.param('id'));
  }
  load(id) {
    this.discussion = null;
    this.notFound = false;
    app.store.find('discussions', id, {
      include: 'firstPost,firstPost.user'
    }).then(discussion => {
      this.discussion = discussion;
      app.setTitle(discussion.title());
      m.redraw();
    }).catch(() => {
      this.notFound = true;
      m.redraw();
    });
  }
  view() {
    const post = this.discussion && this.discussion.firstPost();
    const author = this.discussion && this.discussion.user();
    return m("div", {
      className: "Page ArticlePage"
    }, m("div", {
      className: "container"
    }, this.notFound ? m(Placeholder, {
      text: app.translator.trans('champis-articles.forum.article_page.not_found')
    }) : !this.discussion ? m(LoadingIndicator, null) : m("div", {
      className: "ArticlePage-content"
    }, m("h1", {
      className: "ArticlePage-title"
    }, this.discussion.title()), m("p", {
      className: "ArticlePage-author"
    }, author && app.translator.trans('champis-articles.forum.article_page.by', {
      username: author.champisDisplayName ? author.champisDisplayName() : author.username()
    }), ' ', editPostButton(post)), post ? m("div", {
      className: "Post-body",
      onclick: e => this.handlePostBodyClick(e, post)
    }, m.trust(post.contentHtml() || '')) : m(LoadingIndicator, null))));
  }

  // Delegated for both of fof/upload's own inline attachment kinds - an
  // article's post body is rendered directly from contentHtml (m.trust
  // above), never through a real CommentPost/Post component, so neither of
  // the two core extension points that normally handle these ever fire for
  // it (confirmed against both source files, not just reasoned about):
  // champis-forum's own showPostImagesInLightbox.js hooks CommentPost's
  // oncreate, and fof/upload's own downloadButtonInteraction.ts hooks
  // Post's oncreate to wire up every [data-fof-upload-download-uuid]
  // download button's click (building the /fof/download URL, denial
  // alert, the works) - without it those buttons are inert <div>s with no
  // click behavior of their own at all, reported directly as "files that
  // don't open".
  handlePostBodyClick(e, post) {
    this.openImageLightbox(e);
    this.openFileDownload(e, post);
  }

  // Opens fof/upload's own inline image previews (same markup a regular
  // discussion post has - <a class="FoFUpload--Upl-Image-Preview-Link">) in
  // champis-forum's PostImageLightbox, matching a regular discussion's own
  // click-to-zoom lightbox - requested directly. The gallery is just this
  // one post's own images, scanned from the DOM at click time rather than
  // pulled from the discussion's own `images` field (champis-forum's
  // extend.php) the way a regular discussion's lightbox does - simpler, and
  // correct here without needing that field: unlike a regular discussion's
  // own post stream, nothing about this page is windowed/virtualized, so
  // every image already sits in the DOM to scan, no risk of missing one
  // that hasn't been mounted yet.
  //
  // window.flarum.reg.get(...) rather than a static import: extensions
  // can't import each other's uncompiled JS source directly (confirmed
  // elsewhere this session, e.g. champis-forum's DiscussionFilters.js doing
  // the same for champis-species' SpeciesSearchWidget) - PostImageLightbox
  // itself is reused as-is here (not a per-extension copy the way
  // SpeciesGalleryModal/PaintingsGalleryModal are), since nothing about it
  // needs to differ for an article - it comes with champis-theme's own
  // shared .PostImageLightbox-* CSS regardless of which extension opens it.
  openImageLightbox(e) {
    const target = e.target;
    const link = target.closest('.FoFUpload--Upl-Image-Preview-Link');
    if (!link) return;
    const PostImageLightbox = window.flarum.reg.get('champis-forum', 'forum/components/PostImageLightbox');
    if (!PostImageLightbox) return;
    e.preventDefault();
    const currentTarget = e.currentTarget;
    const links = Array.from(currentTarget.querySelectorAll('.FoFUpload--Upl-Image-Preview-Link'));
    const images = links.map(l => ({
      url: l.href,
      alt: l.querySelector('img')?.alt || ''
    }));
    app.modal.show(PostImageLightbox, {
      images,
      index: links.indexOf(link)
    });
  }

  // Same URL-building and permission gating as fof/upload's own
  // downloadButtonInteraction.ts (uuid + this post's own id + the current
  // session's csrf token, opened via window.open() rather than navigating
  // - a real download response, not a page) - just triggered from a plain
  // delegated click here instead of a Post-only oncreate. Skips that
  // file's own visual "locked" treatment (swapping the icon, appending a
  // reason line) for scope - a denied click here still shows the same
  // error alert and downloads nothing, just without pre-emptively greying
  // the button out before it's clicked.
  openFileDownload(e, post) {
    const target = e.target.closest('[data-fof-upload-download-uuid]');
    if (!target) return;
    e.preventDefault();
    const canDownload = app.forum.attribute('fof-upload.canDownload');
    const canDownloadInTag = this.discussion?.attribute('canDownloadFiles');
    const denied = !canDownload || canDownloadInTag === false;
    if (denied) {
      app.alerts.show({
        type: 'error'
      }, app.translator.trans('fof-upload.forum.states.unauthorized', {}, true));
      return;
    }
    const uuid = target.dataset.fofUploadDownloadUuid || '';
    const url = app.forum.attribute('apiUrl') + '/fof/download/' + encodeURIComponent(uuid) + '/' + encodeURIComponent(post.id() ?? '') + '/' + encodeURIComponent(app.session.csrfToken ?? '');
    window.open(url);
  }
}
flarum.reg.add('champis-articles', 'forum/components/ArticlePage', ArticlePage);